import React, { Component } from 'react';
import styled, { keyframes } from 'styled-components';
import PropTypes from 'prop-types';

import Message from './Message';
import { isShowingButton } from '../../reducers/history/utils';
import HistoryLoadingSpinner from './HistoryLoadingSpinner';

const slideUpAnimation = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const MessageHistoryWrapper = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  overflow: hidden;
  width: 100%;
  background-color: white;
  border-top-left-radius: 30px 15px;
  border-top-right-radius: 30px 15px;
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 16px;
  margin-top: ${props => (props.hasHeader ? '-30px' : '0')};
  position: relative;
  z-index: 1;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
  animation: ${props => (props.isIntroTransitioning ? slideUpAnimation : 'none')} 0.4s ease-out;
  padding-bottom: ${props => (props.buttonCentered ? '0' : '80px')};
  transition: padding-bottom 0.3s ease;
`;

const Spacer = styled.div`
  padding-top: 24px;
`;

const Scroller = styled.div`
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  background-color: white;
`;

const Messages = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 110%;
  min-height: calc(100% + 32px);
  justify-content: flex-end;
  overflow-y: visible;
  background-color: white;
`;

// Record whether the user is scrolled to the bottom of the chat view. No need to store this in
// state as that would cause unnecessary re-renders.
let lockedToBottom = true;

const scrollerRef = React.createRef();

// This function must be called when any content in the chat window changes height, so that the
// scroll position can be updated
export const updateScroll = (forceScrollToBottom = false) => {
  const scroller = scrollerRef.current;
  if (scroller && (lockedToBottom || forceScrollToBottom)) {
    scroller.scrollTop = scroller.scrollHeight - scroller.clientHeight;
  }
};

// This component is used by Synapse, do not update its props without. All
class MessageHistory extends Component {
  static propTypes = {
    activities: PropTypes.array.isRequired,
    isFetchingPreviousActivity: PropTypes.bool.isRequired,
    hasPreviousActivitiesToLoad: PropTypes.bool.isRequired,
    onLoadPreviousActivities: PropTypes.func.isRequired,
    messageWrapper: PropTypes.func,
    user: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string
    }),
    isIntroTransitioning: PropTypes.bool.isRequired,
    chatInput: PropTypes.node,
    buttonCentered: PropTypes.bool,
    hasHeader: PropTypes.bool
  };

  static defaultProps = {
    hasHeader: false,
    isIntroTransitioning: false,
    buttonCentered: false
  };

  // A "block" is a group of messages from the same sender (bot or user). For rendering we need to
  // know whether a message is the start / middle / end of a block or if we've got a single message
  // block.
  getPositionInBlock = i => {
    const { activities } = this.props;
    const recipient = this.activityRecipient(activities[i]);
    const previousRecipient = this.activityRecipient(activities[i - 1]);
    const nextRecipient = this.activityRecipient(activities[i + 1]);

    // Start of conversation OR previous activity was not a message
    if (!previousRecipient) {
      return nextRecipient === recipient ? 'start' : 'single';

      // End of conversation OR next activity is not a message
    } else if (!nextRecipient) {
      return previousRecipient === recipient ? 'end' : 'single';

      // Single block message
    } else if (previousRecipient !== recipient && nextRecipient !== recipient) {
      return 'single';

      // End of a block
    } else if (previousRecipient === recipient && nextRecipient !== recipient) {
      return 'end';

      // Start of a block
    } else if (previousRecipient !== recipient && nextRecipient === recipient) {
      return 'start';
    }

    return 'middle';
  };

  activityRecipient = activity => {
    // No next or previous activity, or it wasn't a message
    if (!activity || (activity && activity.type !== 'message')) {
      return null;
    }

    // Activities sent by the user in a session don't have a recipient
    if (!activity.recipient) {
      return null;
    }

    return activity.recipient.id;
  };

  handleScroll = e => {
    const scroller = scrollerRef.current;

    const { hasPreviousActivitiesToLoad, isFetchingPreviousActivity } = this.props;

    if (hasPreviousActivitiesToLoad && !isFetchingPreviousActivity) {
      const { scrollTop } = e.target;
      const reachTop = scrollTop < 10;
      if (reachTop) {
        this.props.onLoadPreviousActivities();
      }
    }

    lockedToBottom =
      Math.abs(scroller.scrollTop + scroller.clientHeight - scroller.scrollHeight) < 5;
  };

  componentDidUpdate() {
    updateScroll();
  }

  render() {
    const {
      activities,
      isFetchingPreviousActivity,
      messageWrapper,
      user,
      isIntroTransitioning,
      chatInput,
      buttonCentered,
      hasHeader
    } = this.props;

    return (
      <MessageHistoryWrapper
        isIntroTransitioning={isIntroTransitioning}
        buttonCentered={buttonCentered}
        hasHeader={hasHeader}
      >
        <Scroller ref={scrollerRef} onScroll={this.handleScroll}>
          <Messages id="transcriptView">
            {isFetchingPreviousActivity && <HistoryLoadingSpinner />}
            {!isShowingButton(activities) && <Spacer />}
            {activities.map((activity, i) => (
              <Message
                user={user}
                messageWrapper={messageWrapper}
                scrollerRef={scrollerRef.current}
                key={activity.id || i}
                activity={{
                  ...activity,
                  isLatestMessage: i === activities.length - 1
                }}
                blockPosition={this.getPositionInBlock(i)}
              />
            ))}
            <Spacer />
          </Messages>
        </Scroller>
        {chatInput}
      </MessageHistoryWrapper>
    );
  }
}

export default MessageHistory;



// WEBPACK FOOTER //
// ./src/components/MessageHistory/index.js